import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { MoreVertical, LucideIcon } from "lucide-react";

export interface ActionItem {
  label: string;
  icon?: LucideIcon;
  onClick: () => void;
  disabled?: boolean;
  destructive?: boolean;
}

export interface ActionGroup {
  label?: string;
  items: ActionItem[];
}

interface ActionsDropdownProps {
  actions: (ActionItem | ActionGroup)[];
  align?: "start" | "center" | "end";
  className?: string;
}

export const ActionsDropdown = ({ 
  actions, 
  align = "end", 
  className 
}: ActionsDropdownProps) => {
  const renderActionItem = (action: ActionItem, key: string) => (
    <DropdownMenuItem
      key={key}
      onClick={action.onClick}
      disabled={action.disabled}
      className={action.destructive ? "text-destructive focus:text-destructive" : ""}
    >
      {action.icon && <action.icon className="mr-2 h-4 w-4" />}
      {action.label}
    </DropdownMenuItem>
  );

  const renderContent = () => {
    return actions.map((item, index) => {
      if ('items' in item) {
        // It's a group
        const group = item as ActionGroup;
        return (
          <div key={index}>
            {index > 0 && <DropdownMenuSeparator />}
            {group.label && <DropdownMenuLabel>{group.label}</DropdownMenuLabel>}
            {group.items.map((action, actionIndex) => 
              renderActionItem(action, `${index}-${actionIndex}`)
            )}
          </div>
        );
      } else {
        // It's a single action
        const action = item as ActionItem;
        return renderActionItem(action, index.toString());
      }
    });
  };

  return (
    <div className="flex justify-end">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className={`h-8 w-8 ${className || ""}`} aria-label="Actions">
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align={align}>
          {renderContent()}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};